import {
  Box,
  CircularProgress,
  IconButton,
  Tooltip,
  Typography,
  MenuItem,
  Menu,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import GroupedChatsFolder from "./GroupedChatsFolder";
import { useState } from "react";
import ChatItem from "./ChatItem";
import { ReactComponent as ArchiveIcon } from "../../../assets/images/icons/archive_icon.svg";
import { ReactComponent as RenameIcon } from "../../../assets/images/icons/rename_icon.svg";
import { ReactComponent as DeleteIcon } from "../../../assets/images/icons/delete_icon.svg";
import { ReactComponent as FolderIcon } from "../../../assets/images/icons/folder_icon.svg";

const ChatItemList = ({
  items,
  groupedChats = [],
  sectionLabel,
  onSelectItem,
  activeSessionId,
  handleActionTrigger,
  isLoading,
  onAddGroup,
}) => {
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [menuTargetItem, setMenuTargetItem] = useState(null);
  const [menuTargetType, setMenuTargetType] = useState("chat");
  const [menuTargetGroupId, setMenuTargetGroupId] = useState(null);
  const isMenuOpen = Boolean(menuAnchorEl);

  const handleMenuClick = (event, item, type = "chat", groupId = null) => {
    event.stopPropagation();
    setMenuAnchorEl(event.currentTarget);
    setMenuTargetItem(item);
    setMenuTargetType(type);
    setMenuTargetGroupId(groupId);
  };

  const handleMenuClose = (event) => {
    if (event) event.stopPropagation();
    setMenuAnchorEl(null);
  };

  const handleAction = (actionType, item) => {
    if (handleActionTrigger) {
      handleActionTrigger(actionType, item, sectionLabel, menuTargetGroupId);
    }
    handleMenuClose();
  };

  return (
    <Box className="history-section">
      <Box className="history-title-header">
        <Typography className="history-text">{sectionLabel}</Typography>

        {sectionLabel === "History" && onAddGroup && (
          <Tooltip title="Add new group" placement="top">
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                onAddGroup();
              }}
              sx={{
                width: 18,
                height: 18,
                backgroundColor: "#077EE0",
                color: "#fff",
                transition:
                  "transform 0.2s ease, box-shadow 0.2s ease, background-color 0.3s ease",

                "&:hover": {
                  backgroundColor: "#0669c0",
                  transform: "translateY(-2px) scale(1.08)",
                  boxShadow: "0 4px 10px rgba(7, 126, 224, 0.35)",
                },

                "&:active": {
                  transform: "scale(0.95)",
                },
              }}
            >
              <AddIcon
                sx={{
                  fontSize: 14,
                  transition: "transform 0.2s ease",

                  ".MuiIconButton-root:hover &": {
                    transform: "rotate(90deg)",
                  },
                }}
              />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      {isLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
          <CircularProgress size={22} sx={{ color: "#0669C0" }} />
        </Box>
      ) : groupedChats.length === 0 && items.length === 0 ? (
        <Typography className="history-empty-state">
          No {sectionLabel.toLowerCase()} chats found.
        </Typography>
      ) : (
        <Box>
          {sectionLabel === "History" && groupedChats.length > 0 && (
            <Box className="grouped-folders-section">
              {groupedChats.map((group) => (
                <GroupedChatsFolder
                  key={group.id || group.name}
                  group={group}
                  activeSessionId={activeSessionId}
                  onSelectItem={onSelectItem}
                  handleMenuClick={handleMenuClick}
                />
              ))}
            </Box>
          )}

          {/* Render Ungrouped Chats */}
          {items.length > 0 && (
            <Box className="ungrouped-chats-section">
              {items.map((item) => (
                <ChatItem
                  key={item.session_id}
                  item={item}
                  activeSessionId={activeSessionId}
                  onSelectItem={onSelectItem}
                  handleMenuClick={handleMenuClick}
                />
              ))}
            </Box>
          )}
        </Box>
      )}

      {/* Menu for Chat Items */}
      <Menu
        anchorEl={menuAnchorEl}
        open={isMenuOpen && menuTargetType === "chat"}
        onClose={handleMenuClose}
        onClick={handleMenuClose}
        slotProps={{
          paper: {
            className: "history-menu-paper",
          },
        }}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
      >
        {sectionLabel === "History" ? (
          <MenuItem
            onClick={() => handleAction("archive", menuTargetItem)}
            className="history-menu-item"
          >
            <ArchiveIcon
              style={{ width: "16px", height: "16px", marginRight: "8px" }}
            />
            <Typography className="history-menu-text">Archive</Typography>
          </MenuItem>
        ) : (
          <MenuItem
            onClick={() => handleAction("unarchive", menuTargetItem)}
            className="history-menu-item"
          >
            <ArchiveIcon
              style={{ width: "16px", height: "16px", marginRight: "8px" }}
            />
            <Typography className="history-menu-text">Unarchive</Typography>
          </MenuItem>
        )}
        {menuTargetGroupId ? (
          <MenuItem
            onClick={() => handleAction("removeFromGroup", menuTargetItem)}
            className="history-menu-item"
          >
            <FolderIcon style={{ marginRight: "8px", marginLeft: "-2px" }} />
            <Typography className="history-menu-text">
              Remove from Group
            </Typography>
          </MenuItem>
        ) : (
          sectionLabel === "History" && (
            <MenuItem
              onClick={() => handleAction("moveToGroup", menuTargetItem)}
              className="history-menu-item"
            >
              <FolderIcon style={{ marginRight: "8px", marginLeft: "-2px" }} />
              <Typography className="history-menu-text">
                Move to Group
              </Typography>
            </MenuItem>
          )
        )}
        <MenuItem
          onClick={() => handleAction("rename", menuTargetItem)}
          className="history-menu-item"
        >
          <RenameIcon
            style={{ width: "16px", height: "16px", marginRight: "8px" }}
          />
          <Typography className="history-menu-text">Rename</Typography>
        </MenuItem>
      </Menu>

      {/* Menu for Folder Items */}
      <Menu
        anchorEl={menuAnchorEl}
        open={isMenuOpen && menuTargetType === "folder"}
        onClose={handleMenuClose}
        onClick={handleMenuClose}
        slotProps={{
          paper: {
            className: "history-menu-paper",
          },
        }}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
      >
        <MenuItem
          onClick={() => handleAction("renameFolder", menuTargetItem)}
          className="history-menu-item"
        >
          <RenameIcon
            style={{ width: "16px", height: "16px", marginRight: "8px" }}
          />
          <Typography className="history-menu-text">Rename</Typography>
        </MenuItem>
        <MenuItem
          onClick={() => handleAction("deleteFolder", menuTargetItem)}
          className="history-menu-item"
        >
          <DeleteIcon
            style={{ width: "16px", height: "16px", marginRight: "8px" }}
          />
          <Typography className="history-menu-text">Delete</Typography>
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default ChatItemList;
